import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Button,
  InputAdornment,
  Collapse,
  LinearProgress,
  Dialog,
} from "@mui/material";
import { AddCircle, RemoveCircle, Delete, Lock, Visibility, VisibilityOff, LockOpen, VpnKey } from "@mui/icons-material";
import { Form, Field, useFormState, useForm } from "react-final-form";
import zxcvbn from "zxcvbn";
import { Util } from "../../../lib/util/Util";
import VaultLib from '../../../lib/util/Vault'; // adjust path as needed
import sessionData from "../../../lib/session/sessionData.json";
const config = {
  header: {
    title: "Vault Information",
    addTooltip: "Add Entry",
    removeAllTooltip: "Remove All Entries",
    removeTooltip: "Remove Entry",
    vaultPasswordTooltip: "Set Vault Password",
    emptyMessage: "Click '+' to add an entry like username, website, etc."

  },
  entriesConfig: { name: "Key", value: "Value" },
  field: {
    name: "vaultEntries",
    vault: "vault"
  },
  entryLabelPrefix: "Entry",
};


const styles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  },
  recordBox: {
    mb: 3,
    p: 2,
    pt: 6,
    border: "1px solid #ccc",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    position: "relative",
  },
  entryLabel: {
    position: "absolute",
    top: -10,
    left: 8,
    background: "#fff",
    px: 1,
  },
  removeIcon: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  emptyMessage: {
    mt: 2,
  },
  removeAllButton: {
    color: "red",
  },
  vaultSetupBox: {
    p: 2,
    mb: 3,
    border: "1px dashed gray",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
};

export const getConfig = () => Util.JSON_COPY(config);
export const getStyles = () => Util.JSON_COPY(styles);

// Password strength checker
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: "Too Short", color: "#ccc" };

  const result = zxcvbn(password);

  let label = "Weak";
  if (result.score >= 4) {
    label = "Very Strong";
  } else if (result.score === 3) {
    label = "Strong";
  } else if (result.score === 2) {
    label = "Medium";
  } else {
    label = "Weak";
  }

  const colors = ["#ff1744", "#ff9100", "#ffc400", "#00e676", "#00c853"];
  const color = colors[result.score] || "#ccc";

  return { score: result.score, label, color };
};

const validatePassword= async (values,password )=>{

    let {encryptedVaultKey, salt, vaultKeyIv, checkSum} = values.vault;
    return await VaultLib.verifyMasterPassword(password, [
      values.id,
      sessionData.USER_PROFILE.uid,
    ],salt,encryptedVaultKey, vaultKeyIv);
  
     
}

const hydrateVault= async (values,password,form )=>{
  hydrateStaging(values, form);
  values.vault.staging.vaultPassword=password;  
  let {encryptedVaultKey, salt, vaultKeyIv, checkSum} = values.vault;
  const passwordKey = await VaultLib.getMasterPassword(password,  [
    values.id,
    sessionData.USER_PROFILE.uid,
  ], salt);
      const vaultKey = await VaultLib.decryptVaultKey(
          encryptedVaultKey,
          passwordKey,
          vaultKeyIv
        );
  const decryptedObject = await VaultLib.decryptVaultData(vaultKey, values.vault.content.encryptedData, values.vault.content.iv);
  form.change(`vault.staging.${config.field.name}`, decryptedObject);
}

const hydrateStaging=(values,form)=>{
  values.vault.staging = values.vault.staging?values.vault.staging: {
    "vaultEntries":[],
    "vaultPassword":null,
    "vaultPasswordChangeRequest":false,
    "vaultChangePassword":null,
    "vaultChangePasswordConfirm":null,
    "vaultPasswordKey":null,
    "vaultPasswordHint":null,
    "vaultPasswordSalt":null
  }
 //form.change(`vault.staging`, values.vault.staging);
}

const CONTROLS = Object.freeze({
  CONTENT: "CONTENT",
  LOCK_CONTROL: "LOCK_CONTROL",
  RESET_VAULT_KEY: "RESET_VAULT_KEY",
  ADD_RECORD: "ADD_RECORD",
  REMOVE_RECORD: "REMOVE_RECORD",
  REMOVE_ALL_RECORDS: "REMOVE_ALL_RECORDS"
});

const VaultKeyValueEntriesWiz = ({ config = getConfig(), styles = getStyles() }) => {
  const { values } = useFormState();
  const form = useForm();
  //const fieldsConfig = config.entriesConfig;

  const [settingVault, setSettingVault] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showMasterPassword, setShowMasterPassword] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [vaultUnlocked, setVaultUnlocked] = useState(false);



  const [confirmPasswordInput, setConfirmPasswordInput] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [unlockPasswordInput, setUnlockPasswordInput] = useState("");
  const [unlockError, setUnlockError] = useState("");

  const [from, setFrom] = useState("")


  const [failedAttempts, setFailedAttempts] = useState(0);


  const [mismatchMessage, setMismatchMessage] = useState("Passwords do not match.");
  const [vaultPasswordResetMesssage, setVaultPasswordResetMesssage] = useState("Invalid Information");

  // is vault kety already set  ready to go                                
  const isVaultSet = useMemo(() => !!values?.vault?.encryptedVaultKey, [values?.vault?.encryptedVaultKey]);


  const handleAddRecord = (form) => {
    const currentRecrods = values.vault.staging[config.field.name] || [];
    const newEntry = Object.keys(config.entriesConfig).reduce((acc, key) => {
      acc[key] = ""; // Initialize with empty string or any default value
      return acc;
    }, {});
    form.change(`vault.staging.${config.field.name}`, [...currentRecrods, newEntry]);
  };

  const handleRemoveRecord = (form, index) => {
    const currentRecrods = values.vault.staging[config.field.name] || [];
    const updatedRecords = currentRecrods.filter((_, i) => i !== index);
    form.change(`vault.staging.${config.field.name}`, updatedRecords);
  };

  const handleRemoveAllRecords = (form) => {
    form.change(`vault.staging.${config.field.name}`, []);
  };

  const updateRecord = (form, index, field, value) => {
    const currentRecrods = values.vault.staging[config.field.name] || [];
    const updatedRecords = [...currentRecrods];
    updatedRecords[index][field] = value;
    form.change(`vault.staging.${config.field.name}`, updatedRecords);
  };

  const generateFormFields = (config, record, index, form) => {
    return Object.keys(config.entriesConfig).map((key) => {
      return (
        <TextField
          key={`${index}-${key}`}
          label={config.entriesConfig[key]}
          value={record[key] || ""}
          onChange={(e) =>
            updateRecord(form, index, key, e.target.value)
          }
          type={showPassword ? "text" : "password"}
          fullWidth
        />

      )
    });
  };

  const handleSetVaultPassword = async () => {

    let staging = {
      ...values.vault.staging,
      vaultChangePassword: values.vault?.staging?.vaultChangePassword,
      vaultChangePasswordConfirm: values.vault?.staging?.vaultChangePasswordConfirm,
      vaultChangePasswordHint: values.vault?.staging?.vaultChangePasswordHint
      
    }
  
    if (!staging.vaultChangePassword || staging.vaultChangePassword.length < 6) {
      setShowPasswordResetDialog(true);
      setVaultPasswordResetMesssage("Password is required and must be at least 6 characters.");
      return;
    }
  
    if (!staging.vaultChangePasswordConfirm) {
      setShowPasswordResetDialog(true);
      setVaultPasswordResetMesssage("Please confirm your vault password.");
      return;
    }
  
    if (staging.vaultChangePassword !== staging.vaultChangePasswordConfirm) {
      setShowPasswordResetDialog(true);
      setVaultPasswordResetMesssage("Passwords do not match.");
      return;
    }
  
    // if (!vaultHint || vaultHint.trim() === "") {
    //   setShowMismatchDialog(true);
    //   setMismatchMessage("Hint is required to help recover your vault.");
    //   return;
    // }
  

  
      //form.change(`_$vault_currentVaultPassword`, values.vault.staging.vaultPassword);
    
      values.vault.staging.vaultPasswordChangeRequest=true;
      if( !!!values.vault.staging.oldVaultPassword){ //only need to copy the first on so subequent set valut password will not override the original password
        values.vault.staging.oldVaultPassword =  values.vault.staging.vaultPassword;
      }
      
      values.vault.staging.vaultPassword = values.vault.staging.vaultChangePassword;
  
      setSettingVault((prev)=>false);  //out of setting vault mode (form completed)
      setVaultUnlocked((prev) => true);  //unlock vault after setting password
    
    
  };


  // Stubbed function to determine state based on control type
function getControlState(controlType) {
  let state = { disabled: false };  // Default state: enabled
  
  switch (controlType) {
    case CONTROLS.CONTENT:
      // Logic for CONTENT control
     // console.log("Handling CONTENT control");
      //Disable if in 
      // settingVault process, 
      // vault is locked
      // no record entry

      state.disabled=settingVault||!vaultUnlocked || !(values.vault.staging[config.field.name] || []).length
      break;

    case CONTROLS.LOCK_CONTROL:
      // Logic for LOCK_CONTROL control
      //console.log("Handling LOCK_CONTROL control");
      //Disabled when
      //vault is a brand new vault no key set yet
      //vault is unLocked
      //vault is locked and in key setting mode => we are in the middle of setting key so locked is not available
      state.disabled=!isVaultSet||vaultUnlocked||(!vaultUnlocked&&settingVault)
      
      break;

    case CONTROLS.RESET_VAULT_KEY:
      // Logic for REST_VAULT_KEY control
      //console.log("Handling RESET_VAULT_KEY control");
      // You can add conditions here based on your application's state
      // Disable whne
      // settingVault process
      // vault is locked
      // brand new vault and not in setting vault mode  (!isVaultSet &&!settingVault)
     

      state.disabled = !vaultUnlocked || settingVault 
      break;

    case CONTROLS.ADD_RECORD:
      // Logic for ADD_RECORD control
      //console.log("Handling ADD_RECORD control");
      // This can be based on whether the user is allowed to add a record or not

      //Disabled when
      //vault is locked
      //or in vaultSetting mode
      state.disabled=!vaultUnlocked||settingVault
      break;

    case CONTROLS.REMOVE_RECORD:
      // Logic for REMOVE_RECORD control
      //console.log("Handling REMOVE_RECORD control");
      // Add conditions here to determine if removing a record is allowed
      break;

    case CONTROLS.REMOVE_ALL_RECORDS:
      // Logic for REMOVE_ALL_RECORDS control
      //console.log("Handling REMOVE_ALL_RECORDS control");
      // You can add specific conditions or checks here
      // Disable when
      // in setting valut
      // vault is locked
      // no record entry

      state.disabled = settingVault||!vaultUnlocked || !(values.vault.staging[config.field.name] || []).length
      break;

    default:
      //console.log("Unknown control type");
      state.disabled = false;  // Default behavior is enabled
      break;
  }

  return state;
}
 
  const { score, label, color } = getPasswordStrength(values.vault.staging?.vaultChangePassword);
  
  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={styles.container}>
        <Typography variant="h6">{config.header.title}</Typography>
        <Box>
          <Tooltip title="Unlock Vault">
            <span>
              <IconButton
                onClick={() => setShowUnlockDialog(true)}
                color="primary"
                disabled={getControlState(CONTROLS.LOCK_CONTROL).disabled}
              >
                <LockOpen />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isVaultSet ? (settingVault ? "Cancel Reset" : "Reset Vault Password") : (settingVault ? "Cancel Add Password" : "Add Vault Password")}>
            <span>
            <IconButton
              onClick={() => {
                if (isVaultSet) {
                  // Existing vault logic
                  if (settingVault) {  //cancel reset
                    // When settingVault is true, revert to old password if available
                    values.vault.staging.vaultPasswordChangeRequest = false;
                    values.vault.staging.vaultPassword = values.vault.staging.oldVaultPassword || values.vault.staging.vaultPassword;
                    setSettingVault(prev => false);
                  } else { //reset vault 
                    // When settingVault is false, request a password change
                    values.vault.staging.vaultPasswordChangeRequest = true;
                    setSettingVault(prev => true);
                    //setShowConfirmDialog(true); // if want to password check for user before change password
                   
                  }
                }else{
                  // brand new vault logic
                  
                  if(settingVault){
                    //in setting vault Key mode => wants to get out of setting vault key mode
                    setSettingVault(prev => false);
                    setVaultUnlocked(prev => true); //disable vaultLock control
                    values.vault.staging.vaultPasswordChangeRequest = false;
                 
                  }else{ 
                    //not in setting vault key mode => wants to get in to the set up the vault key
                    values.vault.staging.vaultPasswordChangeRequest = false;
                    setSettingVault(prev => true);
                    setVaultUnlocked(prev => false); 
                  }
                   
                }
              
                
              }}
              disabled={getControlState(CONTROLS.RESET_VAULT_KEY).disabled}
              color={isVaultSet ? "primary" : "secondary"}
            >
              <VpnKey />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={config.header.addTooltip}>
            <span>
              <IconButton
                onClick={() => handleAddRecord(form)}
                color="primary"
                disabled={getControlState(CONTROLS.ADD_RECORD).disabled}
               
              >
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={config.header.removeAllTooltip}>
            <span>
              <IconButton
                onClick={() => handleRemoveAllRecords(form)}
                sx={styles.removeAllButton}
                disabled={getControlState(CONTROLS.REMOVE_ALL_RECORDS).disabled}
              >
                <RemoveCircle />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={showPassword ? "Hide Values" : "Show Values"}>
            <span>
              <IconButton 
              color="primary"
              disabled={getControlState(CONTROLS.CONTENT).disabled}
              onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}  
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>   
      {/* Vault Password Setup */}
      {/* <Collapse in={!isVaultSet||(isVaultSet && settingVault )}> 
        //show reset screen only if 
          new vault - no vault key (vaultUnlocked = false, isVaultSet = false)
          existing vault - has key wants to reset key (isVaultSet=true, settingVault=true)
      
      */}
      <Collapse in={(!vaultUnlocked && !isVaultSet)|| (isVaultSet && settingVault)}>
        <Box sx={styles.vaultSetupBox}>
          <Typography variant="subtitle1">Set Vault Password</Typography>
          <Field name={`vault.staging.vaultChangePassword`}>
            {({ input }) => {
              return (
                <TextField
                  key="mk1"
                  {...input}
                  label="Vault Password"
                  type={showMasterPassword ? "text" : "password"}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowMasterPassword(!showMasterPassword)} edge="end">
                          {showMasterPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )
            }}
          </Field>

          <Box sx={{ mt: 1}}>
            <LinearProgress
              variant="determinate"
              value={(score / 4) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#eee",
                transition: "all 0.4s ease",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: color,
                  transition: "background-color 0.4s ease, width 0.4s ease",
                },
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, color: color }}>
              {label}
            </Typography>
          </Box>

          <Field name={`vault.staging.vaultChangePasswordConfirm`}>
            {({ input }) => (
              <TextField
               key="mk2"
                {...input}
                label="Confirm Password"
                type={showMasterPassword ? "text" : "password"}
                fullWidth
              
              />
            )}
          </Field>

          <Field name={`vault.staging.vaultChangePasswordHint`}>
            {({ input }) => (
              <TextField
                key="mk3"
                {...input}
                label="Hint (optional)"
                fullWidth
              />
            )}
          </Field>

          <Button
            variant="contained"
            color="primary"
            onClick={async ()=>await handleSetVaultPassword()}
            sx={{ mt: 2 }}
          >
            Set Vault Password
          </Button>
          {values.vault.staging && !isVaultSet && settingVault && <Button onClick={() => {

            setSettingVault(() => false);//exisiting setting vault process
            values.vault.staging = null;
          }}>
            Cancel Create Vault
          </Button>
          }        
          {values.vault.staging && isVaultSet && settingVault && <Button onClick={() => {

              setSettingVault(()=>false);//exisiting setting vault process
            
              if(isVaultSet){ //existing 
                if(values.vault.staging && from=="WIPE"){
                  values.vault.staging=null;
                  setVaultUnlocked(()=>false);
                  
                }else{
                  values.vault.staging.vaultChangePassword=null;
                  values.vault.staging.vaultChangePasswordConfirm=null;
                  values.vault.staging.vaultChangePasswordHint=null;
                  values.vault.staging.vaultPasswordChangeRequest=false;
                  if(!!values.vault.staging.oldVaultPassword){// when user reset the vault key and cancle it. 
                    values.vault.staging.vaultPassword= values.vault.staging.oldVaultPassword;
                  }
           
                }
                // alert(from)            
                // if(from=="CONFIRM_WIPE"){
                  
                //   setSettingVault((prev)=>false);  
                //   setVaultUnlocked((prev) => true);  
                  
                // }else{
                //   values.vault.staging.vaultChangePassword=null;
                //   values.vault.staging.vaultChangePasswordConfirm=null;
                //   values.vault.staging.vaultChangePasswordHint=null;
                //   values.vault.staging.vaultPasswordChangeRequest=false;
                // }
      
               
              }
             
            }}>
              Cancel Vault Password Reset
            </Button>
          }
        </Box>
      </Collapse>

      {/* Entries
        show this only when
          vaultUnlocked //valult is unlock
          settingVault //not in settingVault mode
      
      */}
      {(vaultUnlocked && !settingVault)  && (
        <div>
        <Field name={`vault.staging.vaultEntries`} subscription={{ value: true }}>
          {() => {
            return !!values.vault.staging.vaultEntries && values.vault.staging.vaultEntries.length > 0 ? (
              values.vault.staging.vaultEntries.map((record, index) => (
                <Box key={index} sx={styles.recordBox}>
                  <Typography variant="subtitle1" sx={styles.entryLabel}>
                    {`${config.entryLabelPrefix} (${index + 1})`}
                  </Typography>

                  <Tooltip title={config.header.removeTooltip}>
                  <span>
                    <IconButton
                      onClick={() => handleRemoveRecord(form, index)}
                      color="secondary"
                      sx={styles.removeIcon}
                      disabled={!vaultUnlocked}
                    >
                      <Delete />
                    </IconButton>
                    </span>
                  </Tooltip>

                  {generateFormFields(config, record, index, form)}

                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary" sx={styles.emptyMessage}>
                {config.header.emptyMessage}
              </Typography>
            );
          }}
        </Field></div>)}

      {/* Confirm Current Password Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} PaperProps={{
        sx: {
          borderRadius: 2, // real rounding here
          p: 2,            // padding
          // width
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
        }
      }}>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
          <Typography variant="h6">Confirm Vault Reset</Typography>
          <Typography variant="body2">Enter your current vault password to proceed:</Typography>

          {values.vault.hint && (
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'gray' }}>
              Hint: {values.vault.hint}
            </Typography>
          )}

          <TextField
            label="Current Password"
            type="password"
            value={confirmPasswordInput}
            onChange={(e) => setConfirmPasswordInput(e.target.value)}
            error={!!confirmPasswordError}
            helperText={confirmPasswordError}
            fullWidth
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => {
              setShowConfirmDialog(false);
              setConfirmPasswordInput("");
              setConfirmPasswordError("");
             
            }}>
              Cancel
            </Button>

            <Button variant="contained" onClick={async () => {

              if ( await validatePassword(values,confirmPasswordInput )) {
                //have data is staging means vault value reset
                //clear out all previous form data
                setConfirmPasswordInput("");
                setConfirmPasswordError("");
                setFailedAttempts(0);
                setShowConfirmDialog(false);
                hydrateVault(values, confirmPasswordInput,form)
                //values.vault.staging.vaultPassword=confirmPasswordInput;
                //isVaultSet = false;
                setVaultUnlocked((prev)=>true);
                setSettingVault((prev)=>true);
              } else {
                const newFailed = failedAttempts + 1;
                setFailedAttempts(newFailed);

                if (newFailed >= 5) {
                  setShowConfirmDialog(false);
                  setShowWipeDialog(true);
                  setFrom(()=>"WIPE")
                } else {
                  setConfirmPasswordError("Incorrect password. Please try again.");
                }
              }
            }}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Wipe Vault Dialog */}
      <Dialog open={showWipeDialog} onClose={() => setShowWipeDialog(false)} PaperProps={{
        sx: {
          borderRadius: 2, // real rounding here
          p: 2,            // padding
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
        }
      }}>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
          <Typography variant="h6" color="error">Don't Remember The Vault Key?</Typography>
          <Typography variant="body2" color="textSecondary">
            If you can't recall the vault password, you must wipe your vault to reset it in order to continue use this vault. <b>This action cannot be undone once you submit the change request.</b>
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => {
              setShowWipeDialog(false);
              setFailedAttempts(0);
            }}>
              Cancel
            </Button>

            <Button variant="contained" color="error" onClick={() => {
              
              setShowWipeDialog(false);
              setSettingVault(true);
              setFailedAttempts(0);
           
              if(values.vault.staging){
                 delete values.vault.staging
              }
            
             

            }}>
              Wipe Vault
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={showPasswordResetDialog} onClose={() => setShowPasswordResetDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2, // real rounding here
            p: 2,            // padding
            width: 250,      // width
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'background.paper',
          }
        }}>
        <Box>
          <Typography variant="h6" color="error" sx={{ m: 2 }}>Unable to Proceed</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ m: 2 }}>
          {vaultPasswordResetMesssage}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 2 }}>
            <Button variant="contained" onClick={() => setShowPasswordResetDialog(false)}>
              OK
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={showMismatchDialog} onClose={() => setShowMismatchDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2, // real rounding here
            p: 2,            // padding
            width: 250,      // width
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'background.paper',
          }
        }}>
        <Box>
          <Typography variant="h6" color="error" sx={{ m: 2 }}>Password Mismatch</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ m: 2 }}>
          {mismatchMessage}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 2 }}>
            <Button variant="contained" onClick={() => setShowMismatchDialog(false)}>
              OK
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog 
      open={showUnlockDialog} onClose={() => setShowUnlockDialog(false)}
      PaperProps={{
        sx: {
          borderRadius: 2, // real rounding here
          p: 2,            // padding
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
        }
      }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, width: 300 }}>
          <Typography variant="h6">Unlock Vault</Typography>
          <Typography variant="body2" color="textSecondary">
            Enter your vault password to unlock:
          </Typography>
          {values.vault.hint && (
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'gray' }}>
              Hint: {values.vault.hint}
            </Typography>
          )}
          <TextField
            label="Vault Password"
            type="password"
            value={unlockPasswordInput}
            onChange={(e) => setUnlockPasswordInput(e.target.value)}
            error={!!unlockError}
            helperText={unlockError}
            fullWidth
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => {
              setShowUnlockDialog(false);
              setUnlockPasswordInput("");
              setUnlockError("");
              setFailedAttempts(()=>0);
            }}>
              Cancel
            </Button>

            <Button variant="contained" onClick={async () => {
              //const storedPassword = values[config.field.vaultPasswordName]
              if (await validatePassword(values, unlockPasswordInput)) {
                hydrateVault(values, unlockPasswordInput,form)
                setVaultUnlocked((prev)=>true);
                setShowUnlockDialog((prev)=>false);
                setUnlockPasswordInput("");
                setUnlockError("");
               } else {
               
                setFailedAttempts(()=>failedAttempts+1);
              
                if (failedAttempts >= 5) {
                 // setShowMismatchDialog(()=>false);
                  //setShowConfirmDialog(()=>false);
                  setShowUnlockDialog(()=>false);
                  //setVaultUnlocked((prev)=>false);
                  setShowWipeDialog(()=>true);
                  setFrom(()=>"WIPE");
                } else {
                  setShowMismatchDialog(true);
                  setMismatchMessage("Incorrect password. Please try again.");

                }
                //setUnlockError("Incorrect password. Please try again.");
               }
            }}>
              Unlock
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default VaultKeyValueEntriesWiz;
